import _ from 'lodash';
import { isToday } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { classNames } from '@progress/kendo-react-common';
import { Step, Stepper, StepProps} from '@progress/kendo-react-layout';
import { LoaderOverlay, SuspenseBoundary } from 'components';
import { DisputeEventTypes } from 'enums';
import { useDisputeTracking, useRefreshDisputeTracking } from 'state';
import { DisputeDetail } from 'types';
import styles from './TrackingStep.module.scss';

type TrackingHistoryProps = {
    dispute: DisputeDetail,
};

const TrackingHistory = ({ dispute }: TrackingHistoryProps) => {
    const { disputeNumber } = dispute;
    const refresh = useRefreshDisputeTracking(disputeNumber);

    return (
        <SuspenseBoundary
            errorMessage={'Error retrieving dispute tracking history'}
            loadingComponent={
                <LoaderOverlay isLoading container height="160px">
                    Loading dispute timeline...
                </LoaderOverlay>
            }
            onReset={refresh}
        >
            <TrackingComponent dispute={dispute} />
        </SuspenseBoundary>
    );
};

const TrackingComponent = ({ dispute }: TrackingHistoryProps) => {
    const { disputeNumber } = dispute;
    const tracking = useDisputeTracking(disputeNumber);
    const items = [...tracking].reverse();
    
    return (
        <Stepper
            className={styles.stepper}
            item={TrackingStep}
            items={items}
            linear
            orientation="vertical"
            value={items.length - 1}
        />
    );
};

const EventLabel = {
    [DisputeEventTypes.DisputeCreated]: 'Submitted',
    [DisputeEventTypes.DisputeRoutedToUser]: 'Routed To User',
    [DisputeEventTypes.DisputeRoutedToVendor]: 'Routed To Supplier',
    [DisputeEventTypes.DisputeEscalated]: 'Escalated',
    [DisputeEventTypes.DisputeApproved]: 'Approved',
    [DisputeEventTypes.DisputeDenied]: 'Denied',
    [DisputeEventTypes.DisputeDeleted]: 'Deleted',
    [DisputeEventTypes.NoteAdded]: 'Note Added',
    [DisputeEventTypes.DisputeRoutedBack]: 'Routed Back From Supplier',
    [DisputeEventTypes.DocumentAdded]: 'Document Added',
    [DisputeEventTypes.Reopened]: 'Reopened',
    [DisputeEventTypes.DisputePaid]: 'Paid',
    [DisputeEventTypes.DisputeCompleted]: 'Completed',
};

type TrackingStepProps = {
    eventDate: Date,
    eventTypeId: number,
    assigneeFirstName?: string,
    assigneeLastName?: string,
    note?: string
    updatedByEmailAddress: string,
    updatedByFirstName: string,
    updatedByLastName: string,
} & StepProps | StepProps;

const TrackingStep = (props: TrackingStepProps) => {
    const { current, index, eventDate, note, updatedByFirstName, updatedByLastName } = props;
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    return (
        <Step current={current} index={index}>
            <div className={classNames(styles.step, { [styles.current]: index === 0 })}>
                <div className={styles.trackingHistoryDates}>
                    <div className={styles.circle} />
                    <div className={styles.date}>{isToday(eventDate) ? 'Today' : formatInTimeZone(eventDate, timezone, 'M/d/yy')}</div>
                    <div>{formatInTimeZone(eventDate, timezone, 'h:mm a')}</div>
                </div>
                <div className={styles.trackingHistoryNotes}>
                    <TrackLabel {...props}></TrackLabel>
                    <div className={styles.userName}>
                        {(updatedByFirstName && updatedByLastName) ?
                            `${updatedByFirstName} ${updatedByLastName}`
                        : 
                            "Name Not Found"}
                    </div>
                    <div className={styles.notes}>{note}</div>  
                </div>
            </div>
        </Step>
    )
};

const TrackLabel = ( trackingProps : TrackingStepProps ) => {
    const { eventTypeId, assigneeFirstName, assigneeLastName } = trackingProps;

    switch (eventTypeId) 
    {
        case DisputeEventTypes.DisputeRoutedToUser :
            return (<div className={styles.label}>{`Routed To ${assigneeFirstName ?? 'Unassigned'} ${assigneeLastName ?? ''}`}</div>)
        case DisputeEventTypes.DisputeEscalated:
            return (<div className={styles.escalated}>
                        <div>{`Escalated`}</div>
                        <div>{`Routed To ${assigneeFirstName} ${assigneeLastName}`}</div>
                    </div>)
        default:
            return (<div className={styles.label}>{_.get(EventLabel, eventTypeId, 'Unknown')}</div>) 
    } 
}

export default TrackingHistory;
